/**
 * Clinical record sync utility.
 *
 * Reads FHIR clinical records from the HealthKit native module
 * (native/EleplanBridge/HealthClinical) and sends them to the backend, which
 * stores them in the structured clinical store tagged with the institution
 * each record came from.
 *
 * This runs once per session per project (clinical records change rarely).
 */

import { NativeModules, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store } from '../store';
import debug from './debug';

const THROTTLE_KEY_PREFIX = '@eleplan/clinical_last_sync_';
const THROTTLE_MS = 24 * 60 * 60 * 1000; // once per 24 hours per project
// Lambda's synchronous request payload limit is 6 MB; a full Health Records
// history can exceed it, so upload in batches well under that.
const MAX_BATCH_BYTES = 2.5 * 1024 * 1024;
// A single record larger than this (e.g. an embedded-PDF DocumentReference)
// cannot fit in one request; skip it rather than block every later batch.
const MAX_RECORD_BYTES = 4 * 1024 * 1024;

// UTF-8 byte length (the request body is measured in bytes, not characters).
function utf8Bytes(text: string): number {
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) { bytes += 4; i++; }
    else bytes += 3;
  }
  return bytes;
}

export function batchBySize<T>(items: T[], maxBytes: number): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let size = 0;
  for (const item of items) {
    const itemSize = utf8Bytes(JSON.stringify(item));
    if (current.length && size + itemSize > maxBytes) {
      batches.push(current);
      current = [];
      size = 0;
    }
    current.push(item);
    size += itemSize;
  }
  if (current.length) batches.push(current);
  return batches;
}

interface ClinicalRecord {
  resourceType: string;
  id: string;
  fhirJson: string;
  sourceName?: string;
}

// Admin flag (default off). Unknown or not-yet-fetched counts as OFF so the
// permission sheet and the upload never run before the flag says so.
function appleClinicalEnabled(): boolean {
  const slice = (store.getState() as any)?.featureFlags;
  return Boolean(slice?.fetched) && slice?.flags?.health_apple_clinical_records_enabled === true;
}

/**
 * Shows Apple's Health Records permission sheet. Call from a user action
 * (it is a separate sheet from the regular HealthKit one). Resolves false
 * when the device or build has no Health Records support.
 */
export async function requestClinicalAuthorization(projectId: number | string): Promise<boolean> {
  if (Platform.OS !== 'ios' || !appleClinicalEnabled()) return false;
  if (!isRecipientOfProject(projectId, getSessionIds().cognitoId)) return false;
  const { HealthClinicalModule } = NativeModules;
  if (!HealthClinicalModule?.requestAuthorization) return false;
  try {
    return Boolean(await HealthClinicalModule.requestAuthorization());
  } catch (err: any) {
    debug.warn('Clinical records authorization failed', { message: err?.message });
    return false;
  }
}

// Clinical records may only sync into a plan whose recipient is the signed-in
// user on their own device (their own plan, or a self plan they own). Never
// into a plan for someone else; the backend enforces the same rule.
function isRecipientOfProject(projectId: number | string, cognitoId: string | null): boolean {
  const project = (store.getState() as any)?.projects?.list?.[String(projectId)];
  return !!cognitoId && !!project?.recipient_cognito_id && project.recipient_cognito_id === cognitoId;
}

function getApiBase(): string {
  const { default: cfg } = require('../config');
  return cfg?.apiGateway?.endpoint ?? '';
}

function getAuthToken(): string | null {
  const state = store.getState();
  return (state as any)?.session?.token ?? null;
}

function getSessionIds(): { accountId: string | null; cognitoId: string | null } {
  const state = store.getState();
  return {
    accountId: (state as any)?.session?.account?.account_id ?? null,
    cognitoId: (state as any)?.authentication?.user?.cognito_id ?? null,
  };
}

/**
 * Syncs clinical records for a single project.
 * Throttled to once every 24 hours per project.
 * No-ops silently if not iOS, no native module, or throttle active.
 */
export async function syncClinicalRecords(projectId: number | string): Promise<void> {
  if (Platform.OS !== 'ios' || !appleClinicalEnabled()) return;

  const { HealthClinicalModule } = NativeModules;
  if (!HealthClinicalModule?.queryClinicalRecords) {
    debug.info('documents', 'HealthClinicalModule not available — skipping clinical sync');
    return;
  }

  const throttleKey = `${THROTTLE_KEY_PREFIX}${projectId}`;
  const lastSyncStr = await AsyncStorage.getItem(throttleKey);
  if (lastSyncStr) {
    const lastSync = parseInt(lastSyncStr, 10);
    if (Date.now() - lastSync < THROTTLE_MS) return;
  }

  const { accountId, cognitoId } = getSessionIds();
  const token = getAuthToken();
  if (!accountId || !cognitoId || !token) return;
  if (!isRecipientOfProject(projectId, cognitoId)) return;

  try {
    const result = await HealthClinicalModule.queryClinicalRecords();
    const records: ClinicalRecord[] = Array.isArray(result) ? result : result?.records ?? [];
    const typeErrors: { type?: string; transient?: boolean }[] = Array.isArray(result?.errors) ? result.errors : [];
    // A locked device (protected data unavailable) is transient: sync what came
    // back but retry next session. Other per-type errors are logged only.
    const transientError = typeErrors.some((e) => e?.transient);
    if (typeErrors.length > 0) {
      debug.warn('Clinical record types failed to read', { types: typeErrors.map((e) => e?.type), transientError });
    }
    if (!records || records.length === 0) {
      debug.info('documents', 'No clinical records found in HealthKit');
      return;
    }

    debug.info('documents', 'Syncing clinical records', { count: records.length, projectId });

    const tagged = records
      .map((r: ClinicalRecord) => {
        try {
          return { resource: JSON.parse(r.fhirJson), source_org: r.sourceName ?? '' };
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    if (tagged.length === 0) return;

    const uploadable = (tagged as object[]).filter((item) => utf8Bytes(JSON.stringify(item)) <= MAX_RECORD_BYTES);
    const oversized = tagged.length - uploadable.length;
    if (oversized > 0) debug.warn('Clinical records skipped: too large to upload', { count: oversized });
    if (uploadable.length === 0) return;

    const apiBase = getApiBase();
    let synced = 0;
    let skipped = 0;
    let failed = 0;
    for (const batch of batchBySize(uploadable, MAX_BATCH_BYTES)) {
      const response = await fetch(
        `${apiBase}/health/sync-clinical/${projectId}/${accountId}/${cognitoId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ records: batch }),
        },
      );
      const data = await response.json();
      // A whole-batch failure (transport error or rejection such as 403) stops
      // here with the throttle unstamped, so the next session retries;
      // already-sent batches are idempotent. Per-record failures (failed > 0)
      // are counted and do not block later batches.
      if (!data?.success) return;
      synced += data.synced ?? 0;
      skipped += data.skipped ?? 0;
      failed += data.failed ?? 0;
    }

    if (!transientError) await AsyncStorage.setItem(throttleKey, String(Date.now()));
    debug.info('documents', 'Clinical records synced', { synced, skipped, failed });
  } catch (err: any) {
    debug.warn('Clinical record sync failed', { message: err?.message });
  }
}
